// src/ui/compact-nav.js
//
// v134.7 - a shorter staff app bar (the 15 Oct 2026 look-and-feel review). The bottom bar carries about 24 tabs; Clock
// is the tenth, so a till has to scroll sideways to reach it. With Head Office's switch ON (app_config `nav_compact`,
// default OFF) the bar shows Today, Clock, Rota, Chat and More; More opens a sheet with every OTHER tab that is
// visible right now, in four groups.
//
// THE RULES (pinned by tests/v134-6-compact-nav_test.js):
//   - OFF changes nothing. No class, no style, no button and no listener is added until the switch reads ON, and a
//     missing key, a failed read or any value other than true means OFF.
//   - The real tab buttons stay where they are and stay the ONLY source of truth. Every existing rule (role, store,
//     module, Head Office switches) keeps setting style.display on them exactly as before; this module never writes
//     their display. It reads it, at refresh and again when More opens, and hides the rest with one CSS class.
//   - A choice in the sheet calls the original button's own click handler, so every tab opens exactly as it does now.
//   - The tab that is open but lives in More marks More as active; an amber (attention) tab inside More puts a dot on
//     More.
//
// Wide screens (tills, 1366px) keep the bottom bar, centred: a left rail would have to move #app, the top bar and
// every fixed overlay and footer bar out of its way, which is too much risk for a presentational switch.

var BAR_SIZE = 4;
// The bar, in order. If one of the first four is hidden for this store or role, the next visible one takes its place.
export var BAR_ORDER = ['nav-today', 'nav-clock', 'nav-rota', 'nav-chat', 'nav-news', 'nav-tasks', 'nav-checklist', 'nav-toil'];
export var MORE_GROUPS = [
  { title: 'Comms', ids: ['nav-jack', 'nav-email', 'nav-news', 'nav-tasks', 'nav-calls', 'nav-contacts', 'nav-links'] },
  // v134.11 (Matt, 15 Oct 2026): Requests (customer requests) and Reviews (customer reviews) are store jobs, not pay.
  { title: 'My pay and time', ids: ['nav-toil', 'nav-bonus', 'nav-pettycash', 'nav-awards'] },
  { title: 'Store jobs', ids: ['nav-checklist', 'nav-eod', 'nav-kpi', 'nav-visits', 'nav-requests', 'nav-customer', 'nav-scan', 'nav-vstore'] },
  { title: 'Learning', ids: ['nav-training', 'nav-mydev'] },
];
// A tab added to the bar later and not yet given a group still appears, under this heading, never lost.
var OTHER_TITLE = 'Other';
var CACHE_KEY = 'elc_nav_compact';
var STYLE_ID = 'compactNavStyle';

var _on = false;
var _observer = null;
var _keyHandler = null;
var _opener = null;

function navEl() { return typeof document === 'undefined' ? null : document.getElementById('bottomNav'); }
function isShown(b) { return !!b && b.style.display !== 'none' && !b.hidden; }
function realTabs(nav) {
  return Array.prototype.filter.call(nav.querySelectorAll('.nav-btn'), function (b) { return b.id && b.id !== 'nav-more'; });
}
function tab(nav, id) { var b = document.getElementById(id); return b && nav.contains(b) ? b : null; }
function setClass(el, cls, on) { if (el.classList.contains(cls) !== !!on) el.classList.toggle(cls, !!on); }
function labelOf(b) {
  // The label is the button's own text node; a chat or email pill (a child span with a number) is not part of it.
  var t = '';
  Array.prototype.forEach.call(b.childNodes, function (n) { if (n.nodeType === 3) t += n.textContent; });
  return t.replace(/\s+/g, ' ').trim() || b.id.replace(/^nav-/, '');
}

// The ids on the bar right now (at most four), in bar order.
export function barIds(nav) {
  nav = nav || navEl();
  if (!nav) return [];
  var out = [];
  for (var i = 0; i < BAR_ORDER.length && out.length < BAR_SIZE; i++) {
    if (isShown(tab(nav, BAR_ORDER[i]))) out.push(BAR_ORDER[i]);
  }
  return out;
}

// What More lists right now: [{ title, items: [button] }], only visible tabs that are not on the bar, empty groups dropped.
export function moreGroups(nav) {
  nav = nav || navEl();
  if (!nav) return [];
  var bar = barIds(nav);
  var placed = {};
  var groups = MORE_GROUPS.map(function (g) {
    var items = [];
    g.ids.forEach(function (id) {
      var b = tab(nav, id);
      if (!isShown(b) || bar.indexOf(id) !== -1) return;
      placed[id] = true;
      items.push(b);
    });
    return { title: g.title, items: items };
  }).filter(function (g) { return g.items.length > 0; });
  var other = realTabs(nav).filter(function (b) { return isShown(b) && bar.indexOf(b.id) === -1 && !placed[b.id]; });
  if (other.length) groups.push({ title: OTHER_TITLE, items: other });
  return groups;
}

var CSS = [
  '#bottomNav.nav-compact{overflow:hidden;touch-action:manipulation}',
  '#bottomNav.nav-compact>.nav-btn:not(.nav-cbar):not(#nav-more){display:none!important}',
  '#bottomNav.nav-compact>.nav-btn{flex:1 1 0;min-width:0;min-height:var(--tap,44px);font-size:var(--fs-xs,11px);letter-spacing:0;position:relative}',
  '#bottomNav.nav-compact>[data-cslot="1"]{order:1}#bottomNav.nav-compact>[data-cslot="2"]{order:2}',
  '#bottomNav.nav-compact>[data-cslot="3"]{order:3}#bottomNav.nav-compact>[data-cslot="4"]{order:4}',
  '#bottomNav.nav-compact>#nav-more{order:5}',
  'body.nav-compact-on #navFade{display:none}',
  '#nav-more svg circle{fill:currentColor;stroke:none}',
  '#nav-more .nav-more-dot{display:none;position:absolute;top:8px;left:calc(50% + 8px);width:9px;height:9px;border-radius:var(--r-pill,999px);background:var(--ui-warn,#b45309);box-shadow:0 0 0 2px var(--ui-surface,#fff)}',
  '#nav-more.nav-more-has-dot .nav-more-dot{display:block}',
  '@media (min-width:1024px){#bottomNav.nav-compact{justify-content:center}#bottomNav.nav-compact>.nav-btn{flex:0 1 160px}}',
  '.nav-more-back{position:fixed;inset:0;z-index:var(--z-sheet,1000);background:rgba(17,24,20,.45);display:flex;align-items:flex-end;justify-content:center}',
  '.nav-more-sheet{background:var(--ui-surface,#fff);color:var(--ui-ink,#18211c);width:100%;max-width:640px;max-height:80vh;max-height:80dvh;overflow:auto;-webkit-overflow-scrolling:touch;border-radius:var(--r-lg,18px) var(--r-lg,18px) 0 0;box-shadow:var(--sh-3);padding:4px 16px calc(16px + env(safe-area-inset-bottom,0px));box-sizing:border-box}',
  '.nav-more-head{display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;background:var(--ui-surface,#fff);padding:6px 0;z-index:1}',
  '.nav-more-title{font-size:var(--fs-lg,18px);font-weight:800;margin:0}',
  '.nav-more-close{min-width:var(--tap,44px);min-height:var(--tap,44px);border:none;background:none;border-radius:var(--r-pill,999px);font-size:26px;line-height:1;color:var(--ui-ink-2,#5E6863);cursor:pointer;font-family:inherit}',
  '.nav-more-group h3{font-size:var(--fs-xs,11px);text-transform:uppercase;letter-spacing:.06em;color:var(--ui-ink-2,#5E6863);margin:12px 0 6px;font-weight:700}',
  '.nav-more-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:8px}',
  '.nav-more-item{position:relative;min-height:72px;min-width:var(--tap,44px);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;border:1px solid var(--ui-line,#dfe5e1);background:var(--ui-surface,#fff);border-radius:var(--r-md,12px);color:var(--ui-ink,#18211c);font-family:inherit;font-size:var(--fs-sm,13px);font-weight:600;cursor:pointer;padding:8px 4px;-webkit-tap-highlight-color:transparent;box-shadow:var(--sh-1)}',
  '.nav-more-item:active{background:var(--ui-ground,#f4f6f5)}',
  '.nav-more-item[aria-current="page"]{border-color:var(--ui-green,#009E3D);color:var(--ui-ok,#007A2F);background:var(--ui-green-soft,#E6F5EC)}',
  '.nav-more-item svg{width:24px;height:24px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}',
  '.nav-more-item .nav-more-dot{position:absolute;top:8px;inset-inline-end:10px;width:9px;height:9px;border-radius:var(--r-pill,999px);background:var(--ui-warn,#b45309)}',
  '.nav-more-empty{color:var(--ui-ink-2,#5E6863);font-size:var(--fs-sm,13px);padding:16px 0}',
].join('\n');

function injectStyle() {
  if (document.getElementById(STYLE_ID)) return;
  var s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  (document.head || document.body).appendChild(s);
}

var MORE_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>';

function ensureMore(nav) {
  var m = document.getElementById('nav-more');
  if (m) return m;
  m = document.createElement('button');
  m.type = 'button';
  m.id = 'nav-more';
  m.className = 'nav-btn';
  m.setAttribute('aria-haspopup', 'dialog');
  m.setAttribute('aria-expanded', 'false');
  m.setAttribute('aria-controls', 'navMoreSheet');
  m.innerHTML = MORE_SVG + 'More<span class="nav-more-dot" aria-hidden="true"></span>';
  m.addEventListener('click', function () { openMore(); });
  nav.appendChild(m);
  return m;
}

// Bring the bar in line with the real buttons. Writes only what changed, so the observer that calls it settles.
export function refresh() {
  var nav = navEl();
  if (!nav || !_on) return;
  var bar = barIds(nav);
  realTabs(nav).forEach(function (b) {
    var slot = bar.indexOf(b.id);
    setClass(b, 'nav-cbar', slot !== -1);
    var want = slot === -1 ? null : String(slot + 1);
    if (b.getAttribute('data-cslot') !== want) {
      if (want) b.setAttribute('data-cslot', want); else b.removeAttribute('data-cslot');
    }
  });
  var more = ensureMore(nav);
  var active = null;
  realTabs(nav).forEach(function (b) { if (!active && b.classList.contains('active')) active = b; });
  setClass(more, 'active', !!active && bar.indexOf(active.id) === -1);
  var attn = realTabs(nav).some(function (b) { return isShown(b) && bar.indexOf(b.id) === -1 && b.classList.contains('nav-attn'); });
  setClass(more, 'nav-attn', attn);
  setClass(more, 'nav-more-has-dot', attn);
  var label = attn ? 'More tabs, something needs your attention' : 'More tabs';
  if (more.getAttribute('aria-label') !== label) more.setAttribute('aria-label', label);
}

export function isMoreOpen() { return !!document.getElementById('navMoreBack'); }

export function closeMore() {
  var back = document.getElementById('navMoreBack');
  if (_keyHandler) { document.removeEventListener('keydown', _keyHandler, true); _keyHandler = null; }
  if (!back) return;
  back.parentNode.removeChild(back);
  var more = document.getElementById('nav-more');
  if (more) more.setAttribute('aria-expanded', 'false');
  var opener = _opener; _opener = null;
  try { if (opener && opener.focus) opener.focus(); } catch (e) { /* the opener may be gone */ }
}

export function openMore() {
  var nav = navEl();
  if (!nav) return;
  closeMore();
  var groups = moreGroups(nav);
  _opener = document.getElementById('nav-more') || document.activeElement;

  var back = document.createElement('div');
  back.id = 'navMoreBack';
  back.className = 'nav-more-back';
  var sheet = document.createElement('div');
  sheet.id = 'navMoreSheet';
  sheet.className = 'nav-more-sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'navMoreTitle');

  var head = document.createElement('div');
  head.className = 'nav-more-head';
  var title = document.createElement('h2');
  title.id = 'navMoreTitle';
  title.className = 'nav-more-title';
  title.textContent = 'More';
  var close = document.createElement('button');
  close.type = 'button';
  close.className = 'nav-more-close';
  close.setAttribute('aria-label', 'Close');
  close.textContent = String.fromCharCode(215);   // the multiplication sign as a close cross
  close.addEventListener('click', function () { closeMore(); });
  head.appendChild(title);
  head.appendChild(close);
  sheet.appendChild(head);

  if (!groups.length) {
    var none = document.createElement('div');
    none.className = 'nav-more-empty';
    none.textContent = 'Everything you can use is on the bar.';
    sheet.appendChild(none);
  }
  groups.forEach(function (g, gi) {
    var sec = document.createElement('section');
    sec.className = 'nav-more-group';
    sec.setAttribute('aria-labelledby', 'navMoreG' + gi);
    var h = document.createElement('h3');
    h.id = 'navMoreG' + gi;
    h.textContent = g.title;
    sec.appendChild(h);
    var grid = document.createElement('div');
    grid.className = 'nav-more-grid';
    g.items.forEach(function (b) {
      var it = document.createElement('button');
      it.type = 'button';
      it.className = 'nav-more-item';
      it.setAttribute('data-target', b.id);
      var icon = b.querySelector('svg');
      if (icon) { var c = icon.cloneNode(true); c.setAttribute('aria-hidden', 'true'); it.appendChild(c); }
      var lab = document.createElement('span');
      lab.textContent = labelOf(b);
      it.appendChild(lab);
      if (b.classList.contains('active')) it.setAttribute('aria-current', 'page');
      if (b.classList.contains('nav-attn')) {
        var dot = document.createElement('span');
        dot.className = 'nav-more-dot';
        dot.setAttribute('aria-hidden', 'true');
        it.appendChild(dot);
        it.setAttribute('aria-label', labelOf(b) + ', needs your attention');
      }
      it.addEventListener('click', function () {
        closeMore();
        // The original tab's own handler: switchScreen, openVisitsTab, openMyDevelopment, a page link...
        var orig = document.getElementById(b.id);
        if (orig) orig.click();
      });
      grid.appendChild(it);
    });
    sec.appendChild(grid);
    sheet.appendChild(sec);
  });

  back.appendChild(sheet);
  back.addEventListener('click', function (e) { if (e.target === back) closeMore(); });
  _keyHandler = function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeMore(); return; }
    if (e.key === 'Tab') {
      // keep focus inside the sheet
      var f = sheet.querySelectorAll('button');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!sheet.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }
  };
  document.addEventListener('keydown', _keyHandler, true);
  document.body.appendChild(back);
  var more = document.getElementById('nav-more');
  if (more) more.setAttribute('aria-expanded', 'true');
  var firstItem = sheet.querySelector('.nav-more-item') || close;
  try { firstItem.focus(); } catch (e) { /* focus is a nicety */ }
}

// Switch the short bar on or off. Turning it off when it was never on does nothing at all.
export function setCompactNav(on) {
  var nav = navEl();
  if (!nav) return false;
  if (on) {
    injectStyle();
    _on = true;
    setClass(nav, 'nav-compact', true);
    if (document.body) setClass(document.body, 'nav-compact-on', true);
    ensureMore(nav);
    refresh();
    if (!_observer && typeof MutationObserver !== 'undefined') {
      // the existing rules (tab shown or hidden, amber, active) all change class or style on the real buttons
      _observer = new MutationObserver(function () { refresh(); });
      _observer.observe(nav, { subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    }
    return true;
  }
  if (!_on) return false;
  _on = false;
  if (_observer) { _observer.disconnect(); _observer = null; }
  closeMore();
  nav.classList.remove('nav-compact');
  if (document.body) document.body.classList.remove('nav-compact-on');
  realTabs(nav).forEach(function (b) { b.classList.remove('nav-cbar'); b.removeAttribute('data-cslot'); });
  var more = document.getElementById('nav-more');
  if (more && more.parentNode) more.parentNode.removeChild(more);
  return false;
}

export function isCompactNav() { return _on; }

// The app_config row ({ key, value }) or a bare value -> on only for true (or the text "true"). Anything else is off.
export function flagOn(row) {
  var v = (row && typeof row === 'object') ? row.value : row;
  return v === true || v === 'true';
}

function lsGet() { try { return localStorage.getItem(CACHE_KEY); } catch (e) { return null; } }
function lsSet(on) {
  try { if (on) localStorage.setItem(CACHE_KEY, '1'); else localStorage.removeItem(CACHE_KEY); } catch (e) { /* storage full or blocked: fine */ }
}

// v136.32 (Matt, 23 Oct 2026: "we cant scroll properly along the bottom of the app" ... "1 on desktop and scroll on
// mobile"): with a mouse the long bar cannot be scrolled (no swipe, the wheel scrolls the page, the scrollbar is
// hidden), so the tabs past the edge were out of reach. A computer or till (a screen at least 1024px wide, or a
// mouse) now always has the short bar; a phone or tablet held upright keeps the long bar it swipes, unless Head
// Office's switch is on, which still makes every device short. Watched, so a resized window or a turned tablet follows.
export var DESKTOP_QUERY = '(min-width: 1024px), (hover: hover) and (pointer: fine)';
var _flag = false;    // Head Office's switch (nav_compact), as last read
var _mq = null;
export function isDesktopScreen() {
  try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && !!window.matchMedia(DESKTOP_QUERY).matches; } catch (e) { return false; }
}
function wanted() { return _flag || isDesktopScreen(); }
// The screen changed (a window resized past 1024px, a tablet turned, a mouse plugged in): short or long again.
export function followScreen() { return setCompactNav(wanted()); }
function watchScreen() {
  if (_mq || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  try {
    _mq = window.matchMedia(DESKTOP_QUERY);
    if (_mq.addEventListener) _mq.addEventListener('change', followScreen); else if (_mq.addListener) _mq.addListener(followScreen);
  } catch (e) { /* an old browser keeps whatever it started with */ }
}

// Called from startApp, never awaited. The last answer is remembered on the device so the short bar shows at once on
// the next start; the server's answer then wins (off is removed straight away). A failed read leaves things as they are.
export function applyCompactNav() {
  try {
    var cached = lsGet() === '1';
    _flag = cached;
    if (wanted()) setCompactNav(true);
    watchScreen();
    if (typeof DB === 'undefined' || !DB || !DB.config || typeof DB.config.get !== 'function') return Promise.resolve(wanted());
    return Promise.resolve(DB.config.get('nav_compact')).then(function (row) {
      _flag = flagOn(row);
      setCompactNav(wanted());
      lsSet(_flag);
      return wanted();
    }).catch(function () { return wanted(); });
  } catch (e) {
    return Promise.resolve(false);   // presentational: never block startApp
  }
}

if (typeof window !== 'undefined') {
  window.applyCompactNav = applyCompactNav;
  window.CompactNav = { apply: applyCompactNav, set: setCompactNav, refresh: refresh, open: openMore, close: closeMore };
}
